import api from './api';

export async function getWorkflowNextStatuses(entityType: string, currentStatus?: string) {
  const res = await api.get(`/workflows/${entityType}/transitions`, {
    params: currentStatus ? { current: currentStatus } : undefined,
  });
  return res.data.data as { nextStatuses: string[]; stages: { key: string; label: string }[] };
}

export async function updateRideStatus(id: string, status: string, extra?: Record<string, unknown>) {
  const res = await api.put(`/rides/${id}`, { status, ...extra });
  return res.data.data;
}

export async function rateRide(id: string, rating: number, review?: string) {
  const res = await api.post(`/rides/${id}/rate`, { rating, review });
  return res.data.data;
}

export async function bookRide(payload: Record<string, unknown>) {
  const res = await api.post('/rides', payload);
  return res.data.data;
}

export async function listRideFilterDrivers(serviceId?: string) {
  const res = await api.get('/rides/drivers', {
    params: serviceId ? { serviceId } : undefined,
  });
  return res.data.data as Array<{
    _id: string;
    firstName: string;
    lastName?: string;
    phone?: string;
    userType: string;
    isActive: boolean;
    isVerified: boolean;
    isBlocked: boolean;
    partnerStatus: string;
  }>;
}

export async function updateFoodOrderStatus(id: string, status: string) {
  const res = await api.put(`/food/orders/${id}`, { status });
  return res.data.data;
}

export async function rateFoodOrder(id: string, rating: number, review?: string) {
  const res = await api.post(`/food/orders/${id}/rate`, { rating, review });
  return res.data.data;
}

export async function updateGroceryOrderStatus(id: string, status: string) {
  const res = await api.put(`/grocery/orders/${id}`, { status });
  return res.data.data;
}

export async function confirmGroceryOrder(orderId: string, accept = true) {
  const res = await api.post(`/grocery/orders/${orderId}/confirm`, { accept });
  return res.data.data;
}

export async function markGroceryOrderReady(orderId: string) {
  const res = await api.post(`/grocery/orders/${orderId}/ready`);
  return res.data.data;
}

export async function cancelGroceryOrder(orderId: string, payload?: { reason?: string }) {
  const res = await api.post(`/grocery/orders/${orderId}/cancel`, payload || {});
  return res.data.data;
}

export async function rateGroceryOrder(id: string, rating: number, review?: string) {
  const res = await api.post(`/grocery/orders/${id}/rate`, { rating, review });
  return res.data.data;
}

export async function getOnDemandBookings(params?: Record<string, string>) {
  const res = await api.get('/on-demand/bookings', { params });
  return res.data;
}

export async function getOnDemandBooking(id: string) {
  const res = await api.get(`/on-demand/bookings/${id}`);
  return res.data.data;
}

export async function bookOnDemand(payload: Record<string, unknown>) {
  const res = await api.post('/on-demand/book', payload);
  return res.data.data;
}

export async function estimateOnDemand(payload: Record<string, unknown>) {
  const res = await api.post('/on-demand/estimate', payload);
  return res.data.data;
}

export async function getOnDemandSlots(params: {
  serviceSlug?: string;
  serviceId?: string;
  date: string;
  durationMin?: number;
  lat?: number;
  lng?: number;
}) {
  const res = await api.get('/on-demand/slots', { params });
  return res.data.data as {
    date: string;
    slots: Array<{ start: string; end: string; label: string; available: boolean; remaining: number }>;
    zoneAvailable: boolean;
  };
}

export async function getOnDemandCancellationPolicy() {
  const res = await api.get('/on-demand/cancellation-policy');
  return res.data.data;
}

export async function rescheduleOnDemandBooking(id: string, scheduledAt: string) {
  const res = await api.patch(`/on-demand/bookings/${id}/reschedule`, { scheduledAt });
  return res.data.data;
}

export async function cancelOnDemandBooking(id: string, reason?: string) {
  const res = await api.post(`/on-demand/bookings/${id}/cancel`, { reason });
  return res.data.data;
}

export async function listOnDemandCategories() {
  const res = await api.get('/on-demand/categories');
  return res.data.data as Array<{
    id: string;
    code: string;
    name: string;
    parentId?: string | null;
    parentCode?: string | null;
  }>;
}

export async function listOnDemandPackagesAdmin(params?: Record<string, unknown>) {
  const res = await api.get('/on-demand/packages', { params });
  return res.data as { data?: unknown[]; packages?: unknown[]; meta?: { total: number } };
}

export async function createOnDemandPackage(payload: Record<string, unknown>) {
  const res = await api.post('/on-demand/packages', payload);
  return res.data.data;
}

export async function updateOnDemandPackage(id: string, payload: Record<string, unknown>) {
  const res = await api.put(`/on-demand/packages/${id}`, payload);
  return res.data.data;
}

export async function deleteOnDemandPackage(id: string) {
  const res = await api.delete(`/on-demand/packages/${id}`);
  return res.data.data;
}

export async function seedOnDemandMasters() {
  const res = await api.post('/on-demand/masters/seed');
  return res.data.data;
}

export async function listOnDemandAddons(params?: Record<string, unknown>) {
  const res = await api.get('/on-demand/addons', { params });
  return res.data.data;
}

export async function upsertOnDemandAddon(payload: Record<string, unknown>) {
  const res = await api.post('/on-demand/addons', payload);
  return res.data.data;
}

export async function updateOnDemandStatus(
  id: string,
  status: string,
  extra?: { otp?: string; reason?: string },
) {
  const res = await api.put(`/on-demand/bookings/${id}`, { status, ...extra });
  return res.data.data;
}

export async function assignOnDemandProvider(id: string, providerId: string) {
  const res = await api.put(`/on-demand/bookings/${id}/assign`, { providerId });
  return res.data.data;
}

export async function rateOnDemand(id: string, rating: number, review?: string) {
  const res = await api.post(`/on-demand/bookings/${id}/rate`, { rating, review });
  return res.data.data;
}

export async function listOnDemandQuotations(params?: Record<string, unknown>) {
  const res = await api.get('/on-demand/quotations', { params });
  return res.data.data;
}

export async function createOnDemandQuotation(payload: Record<string, unknown>) {
  const res = await api.post('/on-demand/quotations', payload);
  return res.data.data;
}

export async function submitOnDemandQuote(id: string, payload: Record<string, unknown>) {
  const res = await api.post(`/on-demand/quotations/${id}/quote`, payload);
  return res.data.data;
}

export async function acceptOnDemandQuotation(id: string) {
  const res = await api.post(`/on-demand/quotations/${id}/accept`);
  return res.data.data;
}

export async function listOnDemandChat(bookingId: string) {
  const res = await api.get(`/on-demand/bookings/${bookingId}/chat`);
  return res.data.data;
}

export async function sendOnDemandChat(bookingId: string, message: string) {
  const res = await api.post(`/on-demand/bookings/${bookingId}/chat`, { message });
  return res.data.data;
}

export async function getEvents(includeInactive = true) {
  const res = await api.get('/events', { params: includeInactive ? { includeInactive: 'true' } : {} });
  return res.data.data;
}

export async function createEvent(payload: Record<string, unknown>) {
  const res = await api.post('/events', payload);
  return res.data.data;
}

export async function updateEvent(id: string, payload: Record<string, unknown>) {
  const res = await api.put(`/events/${id}`, payload);
  return res.data.data;
}

export async function deleteEvent(id: string) {
  const res = await api.delete(`/events/${id}`);
  return res.data.data;
}

export async function getEventBookings(params?: Record<string, string>) {
  const res = await api.get('/events/bookings', { params });
  return res.data;
}

export async function bookEvent(eventId: string, payload: Record<string, unknown>) {
  const res = await api.post(`/events/${eventId}/book`, payload);
  return res.data.data;
}

export async function updateEventBookingStatus(id: string, status: string) {
  const res = await api.put(`/events/bookings/${id}`, { status });
  return res.data.data;
}

export async function checkInEventBooking(id: string, qrCode?: string) {
  const res = await api.post(`/events/bookings/${id}/check-in`, { qrCode });
  return res.data.data;
}

export async function rateEventBooking(id: string, rating: number, review?: string) {
  const res = await api.post(`/events/bookings/${id}/rate`, { rating, review });
  return res.data.data;
}

export async function getTenantServices() {
  const res = await api.get('/admin/services');
  return res.data.data as { _id: string; name: string; slug: string; serviceCode: string }[];
}

export async function getRestaurants(includeInactive = true) {
  const res = await api.get('/food/restaurants', { params: includeInactive ? { includeInactive: 'true' } : {} });
  return res.data.data;
}

export async function createRestaurant(payload: Record<string, unknown>) {
  const res = await api.post('/food/restaurants', payload);
  return res.data.data;
}

export async function updateRestaurant(id: string, payload: Record<string, unknown>) {
  const res = await api.put(`/food/restaurants/${id}`, payload);
  return res.data.data;
}

export async function deleteRestaurant(id: string) {
  const res = await api.delete(`/food/restaurants/${id}`);
  return res.data.data;
}

export async function getRestaurantMenu(restaurantId: string, includeInactive = false) {
  const res = await api.get(`/food/restaurants/${restaurantId}/menu`, {
    params: includeInactive ? { includeInactive: 'true' } : {},
  });
  return res.data.data;
}

export async function createMenuItem(restaurantId: string, payload: Record<string, unknown>) {
  const res = await api.post(`/food/restaurants/${restaurantId}/menu`, payload);
  return res.data.data;
}

export async function updateMenuItem(restaurantId: string, menuItemId: string, payload: Record<string, unknown>) {
  const res = await api.put(`/food/restaurants/${restaurantId}/menu/${menuItemId}`, payload);
  return res.data.data;
}

export async function deleteMenuItem(restaurantId: string, menuItemId: string) {
  const res = await api.delete(`/food/restaurants/${restaurantId}/menu/${menuItemId}`);
  return res.data.data;
}

export type AddonGroupRow = {
  id: string;
  name: string;
  minSelection?: number;
  maxSelection?: number;
  required?: boolean;
  addons: { id: string; name: string; price: number; maxQty?: number; group?: string }[];
};

export async function getRestaurantAddonGroups(restaurantId: string) {
  const res = await api.get(`/food/restaurants/${restaurantId}/addon-groups`);
  return res.data.data as AddonGroupRow[];
}

export async function saveRestaurantAddonGroups(restaurantId: string, groups: AddonGroupRow[]) {
  const res = await api.put(`/food/restaurants/${restaurantId}/addon-groups`, { groups });
  return res.data.data as AddonGroupRow[];
}

export async function reorderMenuItems(restaurantId: string, itemIds: string[]) {
  const res = await api.put(`/food/restaurants/${restaurantId}/menu/reorder`, { itemIds });
  return res.data.data;
}

export async function placeFoodOrder(payload: Record<string, unknown>) {
  const res = await api.post('/food/orders', payload);
  return res.data.data;
}

export async function getGroceryStores(includeInactive = true) {
  const res = await api.get('/grocery/stores', { params: includeInactive ? { includeInactive: 'true' } : {} });
  return res.data.data;
}

export async function createGroceryStore(payload: Record<string, unknown>) {
  const res = await api.post('/grocery/stores', payload);
  return res.data.data;
}

export async function updateGroceryStore(id: string, payload: Record<string, unknown>) {
  const res = await api.put(`/grocery/stores/${id}`, payload);
  return res.data.data;
}

export async function deleteGroceryStore(id: string) {
  const res = await api.delete(`/grocery/stores/${id}`);
  return res.data.data;
}

export async function createGroceryProduct(storeId: string, payload: Record<string, unknown>) {
  const res = await api.post(`/grocery/stores/${storeId}/products`, payload);
  return res.data.data;
}

export async function updateGroceryProduct(productId: string, payload: Record<string, unknown>) {
  const res = await api.put(`/grocery/products/${productId}`, payload);
  return res.data.data;
}

export async function deleteGroceryProduct(productId: string) {
  const res = await api.delete(`/grocery/products/${productId}`);
  return res.data.data;
}

export async function getGroceryProducts(storeId: string) {
  const res = await api.get(`/grocery/stores/${storeId}/products`);
  return res.data.data;
}

export async function placeGroceryOrder(payload: Record<string, unknown>) {
  const res = await api.post('/grocery/orders', payload);
  return res.data.data;
}

export async function getAdminUsers(params?: Record<string, string | number>) {
  const res = await api.get('/admin/users', { params: { limit: 200, ...params } });
  return (res.data?.data || []) as { _id: string; firstName?: string; lastName?: string; phone?: string; email?: string; userType?: string }[];
}

export async function createAdminUser(payload: Record<string, unknown>) {
  const res = await api.post('/admin/users', payload);
  return res.data.data;
}

export async function lookupCustomerByPhone(phone: string) {
  const res = await api.get('/admin/users/lookup', { params: { phone, userType: 'customer' } });
  return res.data.data as {
    _id: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  } | null;
}

export async function estimateTransportFare(payload: {
  serviceId: string;
  pickupLat: number;
  pickupLng: number;
  dropoffLat?: number;
  dropoffLng?: number;
  distanceKm: number;
  durationMin: number;
  scheduleType?: 'ride_now' | 'ride_later';
  vehicleTypeCode?: string;
  tripType?: string;
  outstationType?: string;
  tripMode?: string;
  fromCity?: string;
  toCity?: string;
  rentalPackageGroupCode?: string;
}) {
  const res = await api.post('/zones/estimate-fare', payload);
  return res.data.data as {
    subtotal: number;
    tax: number;
    total: number;
    currency?: string;
    zoneName?: string;
    zoneCode?: string;
    surgeMultiplier?: number;
  };
}

export async function assignRideDriver(rideId: string, driverId: string) {
  const res = await api.put(`/rides/${rideId}/assign`, { driverId });
  return res.data.data;
}

export async function getRide(rideId: string) {
  const res = await api.get(`/rides/${rideId}`);
  return res.data.data as {
    _id: string;
    rideNumber?: string;
    status: string;
    rideOtp?: string;
    driverId?: {
      _id?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
    } | string;
    customerId?: { phone?: string; firstName?: string; lastName?: string } | string;
    fare?: { total?: number; baseFare?: number; distanceFare?: number; timeFare?: number; surgeFare?: number };
    metadata?: Record<string, unknown>;
  };
}

export async function assignFoodDeliveryPartner(orderId: string, partnerId: string) {
  const res = await api.put(`/food/orders/${orderId}/assign`, { partnerId });
  return res.data.data;
}

export async function assignGroceryDeliveryPartner(orderId: string, partnerId: string) {
  const res = await api.put(`/grocery/orders/${orderId}/assign`, { partnerId });
  return res.data.data;
}

export async function getConsultations(params?: Record<string, string | number>) {
  const res = await api.get('/consultations', { params });
  return res.data;
}

export async function updateConsultationStatus(id: string, status: string, extra?: Record<string, unknown>) {
  const res = await api.put(`/consultations/${id}`, { status, ...extra });
  return res.data.data;
}

export async function assignConsultationProvider(id: string, providerId: string) {
  const res = await api.put(`/consultations/${id}/assign`, { providerId });
  return res.data.data;
}

export async function getHubBookings(params?: Record<string, string | number>) {
  const res = await api.get('/hub-bookings', { params });
  return res.data;
}

export async function updateHubBookingStatus(id: string, status: string) {
  const res = await api.put(`/hub-bookings/${id}`, { status });
  return res.data.data;
}

export async function assignHubBookingProvider(id: string, providerId: string) {
  const res = await api.put(`/hub-bookings/${id}/assign`, { providerId });
  return res.data.data;
}

// —— Transport lifecycle (NavnGo parity) ——

export async function respondToRideOffer(rideId: string, accept: boolean) {
  const res = await api.post(`/rides/${rideId}/respond`, { accept });
  return res.data.data;
}

export async function arriveAtPickup(rideId: string) {
  const res = await api.post(`/rides/${rideId}/arrived`);
  return res.data.data;
}

export async function startRideTrip(rideId: string, otp?: string) {
  const res = await api.post(`/rides/${rideId}/start`, otp ? { otp } : {});
  return res.data.data;
}

export async function completeRideTrip(rideId: string, payload?: Record<string, unknown>) {
  const res = await api.post(`/rides/${rideId}/complete`, payload || {});
  return res.data.data;
}

export async function cancelRide(rideId: string, payload?: Record<string, unknown>) {
  const res = await api.post(`/rides/${rideId}/cancel`, payload || {});
  return res.data.data;
}

export async function updateRideTip(rideId: string, tip: number) {
  const res = await api.post(`/rides/${rideId}/tip`, { tip });
  return res.data.data;
}

export async function requestRideLocationChange(rideId: string, newAddress: Record<string, unknown>) {
  const res = await api.post(`/rides/${rideId}/location-change`, newAddress);
  return res.data.data;
}

export async function respondRideLocationChange(rideId: string, accept: boolean, newAddress?: Record<string, unknown>) {
  const res = await api.post(`/rides/${rideId}/location-change/respond`, { accept, newAddress });
  return res.data.data;
}

export async function getInProgressRide() {
  const res = await api.get('/rides/in-progress');
  return res.data.data;
}

export async function getRideMessages(rideId: string) {
  const res = await api.get(`/rides/${rideId}/messages`);
  return res.data.data;
}

export async function sendRideMessage(rideId: string, message: string) {
  const res = await api.post(`/rides/${rideId}/messages`, { message });
  return res.data.data;
}

export async function triggerRideSos(rideId: string) {
  const res = await api.post(`/rides/${rideId}/sos`);
  return res.data.data;
}

export async function dispatchRide(payload: Record<string, unknown>) {
  const res = await api.post('/rides/dispatch', payload);
  return res.data.data;
}

export async function listFavouritePlaces() {
  const res = await api.get('/favourites');
  return res.data.data;
}

export async function createFavouritePlace(payload: Record<string, unknown>) {
  const res = await api.post('/favourites', payload);
  return res.data.data;
}

export async function updateFavouritePlace(id: string, payload: Record<string, unknown>) {
  const res = await api.put(`/favourites/${id}`, payload);
  return res.data.data;
}

export async function deleteFavouritePlace(id: string) {
  const res = await api.delete(`/favourites/${id}`);
  return res.data.data;
}

export async function listSosContacts() {
  const res = await api.get('/sos-contacts');
  return res.data.data;
}

export async function createSosContact(payload: Record<string, unknown>) {
  const res = await api.post('/sos-contacts', payload);
  return res.data.data;
}

export async function deleteSosContact(id: string) {
  const res = await api.delete(`/sos-contacts/${id}`);
  return res.data.data;
}

// —— Food cart & lifecycle (NavnEat parity) ——

export async function getFoodCart() {
  const res = await api.get('/food/cart');
  return res.data.data;
}

export async function addFoodCartItem(payload: Record<string, unknown>) {
  const res = await api.post('/food/cart/items', payload);
  return res.data.data;
}

export async function updateFoodCartItem(menuItemId: string, quantity: number, deliveryCoords?: { lat: number; lng: number }) {
  const res = await api.put(`/food/cart/items/${menuItemId}`, { quantity, deliveryCoords });
  return res.data.data;
}

export async function clearFoodCart() {
  const res = await api.delete('/food/cart');
  return res.data.data;
}

export async function applyFoodCartCoupon(promoCode: string, deliveryCoords?: { lat: number; lng: number }) {
  const res = await api.post('/food/cart/coupon', { promoCode, deliveryCoords });
  return res.data.data;
}

export async function removeFoodCartCoupon(deliveryCoords?: { lat: number; lng: number }) {
  const res = await api.delete('/food/cart/coupon', { data: { deliveryCoords } });
  return res.data.data;
}

export async function previewFoodCheckout(lat?: number, lng?: number) {
  const res = await api.get('/food/cart/preview', { params: { lat, lng } });
  return res.data.data;
}

export async function placeFoodOrderFromCart(payload: Record<string, unknown>) {
  const res = await api.post('/food/orders/from-cart', payload);
  return res.data.data;
}

export async function getFoodOrderDetail(orderId: string) {
  const res = await api.get(`/food/orders/${orderId}/detail`);
  return res.data.data;
}

export async function confirmFoodOrder(orderId: string, accept = true) {
  const res = await api.post(`/food/orders/${orderId}/confirm`, { accept });
  return res.data.data;
}

export async function markFoodOrderReady(orderId: string) {
  const res = await api.post(`/food/orders/${orderId}/ready`);
  return res.data.data;
}

export async function respondFoodDispatch(orderId: string, accept: boolean) {
  const res = await api.post(`/food/orders/${orderId}/driver-response`, { accept });
  return res.data.data;
}

export async function verifyFoodPickup(orderId: string, otp: string) {
  const res = await api.post(`/food/orders/${orderId}/pickup-verify`, { otp });
  return res.data.data;
}

export async function verifyFoodDelivery(orderId: string, otp: string) {
  const res = await api.post(`/food/orders/${orderId}/delivery-verify`, { otp });
  return res.data.data;
}

export async function cancelFoodOrder(orderId: string, payload?: Record<string, unknown>) {
  const res = await api.post(`/food/orders/${orderId}/cancel`, payload || {});
  return res.data.data;
}

export async function refundFoodOrder(orderId: string, payload?: { reason?: string; amount?: number }) {
  const res = await api.post(`/food/orders/${orderId}/refund`, payload || {});
  return res.data.data;
}

export async function refundGroceryOrder(orderId: string, payload?: { reason?: string; amount?: number }) {
  const res = await api.post(`/grocery/orders/${orderId}/refund`, payload || {});
  return res.data.data;
}

export async function getPorterVehicles(zoneId?: string) {
  const res = await api.get('/porter/vehicles', { params: { active: true, zoneId } });
  return (res.data?.data || []) as Array<{
    _id: string;
    name: string;
    image?: string;
    maxWeightKg?: number;
    helpersMax?: number;
    baseFare?: number;
    perKm?: number;
    capacityKg?: number;
    maxStops?: number;
  }>;
}

export async function getPorterCategories(zoneId?: string) {
  const res = await api.get('/porter/categories', { params: { active: true, zoneId } });
  return (res.data?.data || []) as Array<{
    _id: string;
    name: string;
    icon?: string;
    image?: string;
    specialHandlingDefaults?: string[];
  }>;
}

export async function getPorterPackages(zoneId?: string) {
  const res = await api.get('/porter/packages', { params: { active: true, zoneId } });
  return (res.data?.data || []) as Array<{
    _id: string;
    name: string;
    weightKg?: number;
    image?: string;
    categoryId?: string | { _id?: string };
    specialHandling?: string[];
    insuranceEligible?: boolean;
  }>;
}

export async function estimatePorterFare(payload: Record<string, unknown>) {
  const res = await api.post('/porter/estimate', payload);
  return res.data.data;
}

export async function placePorterOrder(payload: Record<string, unknown>) {
  const res = await api.post('/porter/orders', payload);
  return res.data.data;
}

export async function assignPorterPartner(orderId: string, partnerId: string) {
  const res = await api.put(`/porter/orders/${orderId}/assign`, { partnerId });
  return res.data.data;
}
